import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { ReqEnum } from 'const/requestStatus';
import {
  BOOKED_SHIFT_COLOR,
  ONLINE_COLOR,
  OFFLINE_COLOR,
  ASSIGNED_COLOR,
} from './constants';
import { settings } from 'settings';
import Fetch from 'utils/fetch';
import Alert from 'components/alerts/Alert';
import Modal from '../Modal';
import { CourierJson, CourierStatus } from '../../../types/bundleTypes';
import { useFetchShiftByRegion } from '../../../hooks/useFetchShift';
import { ShiftCourierJson, ShiftStatusType } from '../../../const/ShiftConst';
import './SelectCourierModal.scss';
import { compareBy } from '../../../utils/array';
import { Region } from '../../../types/orderTypes';
import Checkbox from '../../inputs/Checkbox/Checkbox';
import { useGetCouriers } from './hooks';
import CourierList from './CourierList';
import ModalComponent from 'ReusableComponents/Modal/ModalComponent';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';

function filterCouriers(
  couriers: CourierJson[],
  courierFilter: string
): CourierJson[] {
  return couriers.filter(
    ({ firstName, lastName, phoneNumber, companyName }) => {
      const searchText = courierFilter.toLowerCase();
      return (
        (firstName || '').toLowerCase().includes(searchText) ||
        (lastName || '').toLowerCase().includes(searchText) ||
        (companyName || '').toLowerCase().includes(searchText) ||
        (phoneNumber || '').toLowerCase().includes(searchText)
      );
    }
  );
}

function sortCouriers(
  couriers: CourierJson[],
  assignedCourierIds: Set<number>,
  bookedCourierIds: Set<number>
): CourierJson[] {
  function compareCouriersByBooked(a: CourierJson, b: CourierJson): number {
    const courierAIsBooked = bookedCourierIds.has(a.id);
    const courierBIsBooked = bookedCourierIds.has(b.id);
    if (courierAIsBooked && !courierBIsBooked) {
      return -1;
    } else if (courierBIsBooked && !courierAIsBooked) {
      return 1;
    }
    return 0;
  }

  function compareCouriersByStatus(a: CourierJson, b: CourierJson): number {
    if (
      a.status === CourierStatus.Available &&
      b.status !== CourierStatus.Available
    ) {
      return -1;
    } else if (
      b.status === CourierStatus.Available &&
      a.status !== CourierStatus.Available
    ) {
      return 1;
    }
    return 0;
  }

  function compareCouriersByAssignedToBundle(
    a: CourierJson,
    b: CourierJson
  ): number {
    const courierAIsAssigned = assignedCourierIds.has(a.id);
    const courierBIsAssigned = assignedCourierIds.has(b.id);
    return courierAIsAssigned && !courierBIsAssigned
      ? 1
      : courierBIsAssigned && !courierAIsAssigned
      ? -1
      : 0;
  }

  function compareCouriersByName(a: CourierJson, b: CourierJson): number {
    return a.firstName.localeCompare(b.firstName);
  }

  return couriers.sort(
    compareBy([
      compareCouriersByAssignedToBundle,
      compareCouriersByBooked,
      compareCouriersByStatus,
      compareCouriersByName,
    ])
  );
}

interface ProposeToCourierResponse {
  success: boolean;
}

export default function SelectCourierModal({
  bundleId,
  onCloseCourierModal,
  onSelectedCourier,
  assignedCourierId,
  region,
  selectedDate,
}: {
  bundleId: string;
  onCloseCourierModal: () => void;
  onSelectedCourier: (courier: CourierJson) => void;
  assignedCourierId?: number;
  region: Region;
  selectedDate?: Dayjs;
}): JSX.Element {
  useEffect(() => {
    function escPressed(event) {
      if (event.keyCode === 27) {
        onCloseCourierModal();
      }
    }

    window.addEventListener('keydown', escPressed);
    return () => window.removeEventListener('keydown', escPressed);
  }, [onCloseCourierModal]);

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [courierFilter, setCourierFilter] = useState<string>('');
  const [selectedCourier, setSelectedCourier] = useState<CourierJson>(null);
  const { isLoading, couriers, assignedCourierIds } = useGetCouriers();

  const startDate: Dayjs = selectedDate ? selectedDate : dayjs(new Date());
  const endDate: Dayjs = dayjs(startDate).add(1, 'day');

  const [ShiftCourierWithDateList, fetchStatus] = useFetchShiftByRegion(
    startDate,
    endDate,
    region
  );

  const [sortedCouriers, setSortedCouriers] = useState<CourierJson[]>([]);
  const [bookedCourierIds, setBookedCourierIds] = useState<Set<number>>(
    new Set()
  );
  const [showOnlyBookedCouriers, toggleShowBookedCouriers] =
    useState<boolean>(false);

  useEffect(() => {
    const shiftCouriersToday =
      fetchStatus === ReqEnum.SUCCESS
        ? ShiftCourierWithDateList[0].courierDayBooked
        : [];
    if (shiftCouriersToday.length > 0) {
      setBookedCourierIds(
        new Set(
          shiftCouriersToday
            .filter(
              (shiftCourier: ShiftCourierJson) =>
                shiftCourier.status === ShiftStatusType.BOOKED
            )
            .map((shiftCourier: ShiftCourierJson) => shiftCourier.courierId)
        )
      );
    }
  }, [fetchStatus, ShiftCourierWithDateList]);

  useEffect(() => {
    if (couriers && couriers.length > 0) {
      const couriersForRegion = couriers.filter(courier =>
        courier.regions.some(courierRegion => courierRegion === region)
      );
      const filteredAvailableCouriers: CourierJson[] = filterCouriers(
        couriersForRegion,
        courierFilter
      );
      setSortedCouriers(
        sortCouriers(
          filteredAvailableCouriers,
          assignedCourierIds,
          bookedCourierIds
        )
      );
    }
  }, [couriers, courierFilter, region, bookedCourierIds, assignedCourierIds]);

  useEffect(() => {
    if (assignedCourierId) {
      const assignedCourier = couriers.filter(
        courier => courier.id === assignedCourierId
      )[0];
      if (assignedCourier) {
        setSelectedCourier(assignedCourier);
      }
    }
  }, [assignedCourierId, couriers]);

  const [chooseCourier, setChooseCourier] = useState<{
    status: ReqEnum;
    data?: ProposeToCourierResponse;
  }>({
    status: ReqEnum.INIT,
    data: null,
  });

  function proposeBundleToCourier(courier: CourierJson, bundleId: string) {
    setChooseCourier({ status: ReqEnum.PENDING, data: null });

    Fetch.put({
      url: `${settings.newApiUrl}/bundle/propose-to-courier/${bundleId}/${courier.id}`,
    })
      .then(response => {
        if (response.status >= 400) {
          throw Error();
        }
        return response.json();
      })
      .then((courierResponse: ProposeToCourierResponse) => {
        setChooseCourier({
          status: ReqEnum.SUCCESS,
          data: courierResponse,
        });
        setTimeout(() => {
          onSelectedCourier(null);
        }, 1500);
      })
      .catch(() =>
        setChooseCourier({
          status: ReqEnum.ERROR,
          data: null,
        })
      );
  }

  return (
    <Modal
      title="Available Couriers"
      onClose={() => onCloseCourierModal()}
      isLoading={isLoading}
      buttons={[
        {
          title: 'Select',
          type: 'success',
          onClick: () =>
            assignedCourierIds.has(selectedCourier.id)
              ? setShowConfirmation(true)
              : proposeBundleToCourier(selectedCourier, bundleId),
          loading: chooseCourier.status === ReqEnum.PENDING,
          disabled:
            isLoading ||
            !selectedCourier ||
            chooseCourier.status === ReqEnum.SUCCESS,
        },
      ]}
      closeText="Cancel"
      width={950}
    >
      <ModalComponent
        title="Assign to multiple bundles?"
        open={showConfirmation}
        setOpen={setShowConfirmation}
        className="select-courier-modal__confirm-booking"
      >
        <p>
          This courier is already assigned to a bundle. Do you want to assign
          the courier to multiple bundles?
        </p>
        <div className="select-courier-modal__confirm-booking__buttons">
          <ButtonComponent
            variant="outlined"
            text="Cancel"
            onClick={() => setShowConfirmation(false)}
          />
          <ButtonComponent
            customColor={{ background: '#60A05C', color: '#fff' }}
            variant="contained"
            color="primary"
            text="YES"
            onClick={() => {
              setShowConfirmation(false);
              proposeBundleToCourier(selectedCourier, bundleId);
            }}
          />
        </div>
      </ModalComponent>
      <div className="select-courier-modal">
        <input
          type="text"
          placeholder="Search..."
          onChange={e => setCourierFilter(e.target.value)}
          autoFocus={true}
        />
        <div className="select-courier-modal__list">
          {fetchStatus === ReqEnum.SUCCESS && isLoading === false && (
            <CourierList
              couriers={sortedCouriers}
              selectedCourier={selectedCourier}
              setSelectedCourier={setSelectedCourier}
              bookedCourierIds={bookedCourierIds}
              assignedCourierIds={assignedCourierIds}
              showOnlyBookedCouriers={showOnlyBookedCouriers}
            />
          )}
        </div>

        <div className="select-courier-modal__info">
          <span
            style={{
              borderLeftColor: BOOKED_SHIFT_COLOR,
            }}
          >
            &nbsp; Courier booked for today's shift
          </span>
          <span
            style={{
              borderLeftColor: ONLINE_COLOR,
            }}
          >
            &nbsp; Online on Courier App
          </span>
          <span
            style={{
              borderLeftColor: ASSIGNED_COLOR,
            }}
          >
            &nbsp; Assigned to Bundle
          </span>
          <span
            style={{
              borderLeftColor: OFFLINE_COLOR,
            }}
          >
            &nbsp; Offline
          </span>
        </div>
        <div>
          <Checkbox
            checked={showOnlyBookedCouriers}
            label="Show only booked couriers"
            onClick={() => toggleShowBookedCouriers(!showOnlyBookedCouriers)}
            value="show-booked-couriers"
            className="show-booked-couriers-checkbox"
          />
        </div>
        {chooseCourier.data && !chooseCourier.data.success && (
          <Alert type="danger">
            Something went wrong. The bundle could not be assigned to the
            courier
          </Alert>
        )}
        {chooseCourier.data && chooseCourier.data.success && (
          <Alert type="success">Successfully Proposed To A Courier</Alert>
        )}
      </div>
    </Modal>
  );
}
